/**
 * Shared helpers for Registration pages
 */

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {object} {isValid: boolean, error: string}
 */
export const validateEmailField = (email) => {
  if (!email.trim()) {
    return { isValid: false, error: "Email address is required" };
  }
  const emailRegex = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: "Please enter a valid email address" };
  }
  return { isValid: true, error: "" };
};

/**
 * Validate username format
 * @param {string} username - Username to validate
 * @returns {object} {isValid: boolean, error: string}
 */
export const validateUsernameField = (username) => {
  if (!username.trim()) {
    return { isValid: false, error: "Username is required" };
  }
  const isEmailFormat = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(username);
  if (isEmailFormat) {
    return { isValid: false, error: "Username cannot be an email address" };
  }
  return { isValid: true, error: "" };
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {object} {isValid: boolean, error: string}
 */
export const validatePasswordField = (password) => {
  if (!password.trim()) {
    return { isValid: false, error: "Password is required" };
  }
  if (password.length < 8) {
    return { isValid: false, error: "Password must be at least 8 characters" };
  }
  if (!/\d/.test(password)) {
    return { isValid: false, error: "Password must contain at least one number" };
  }
  if (!/[a-z]/.test(password)) {
    return { isValid: false, error: "Password must contain at least one lowercase letter" };
  }
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, error: "Password must contain at least one uppercase letter" };
  }
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { isValid: false, error: "Password must contain at least one special character" };
  }
  return { isValid: true, error: "" };
};

/**
 * Validate password confirmation
 * @param {string} password - Original password
 * @param {string} confirmPassword - Confirmation password
 * @returns {object} {isValid: boolean, error: string}
 */
export const validateConfirmPasswordField = (password, confirmPassword) => {
  if (!confirmPassword.trim()) {
    return { isValid: false, error: "Please confirm your password" };
  }
  if (password !== confirmPassword) {
    return { isValid: false, error: "Passwords do not match" };
  }
  return { isValid: true, error: "" };
};

/**
 * Build password strength information
 * @param {string} password - Password to analyze
 * @returns {object} {strength: string, score: number, feedback: array}
 */
export const buildPasswordStrengthInfo = (password) => {
  let score = 0;
  const feedback = [];

  if (password.length >= 8) score++;
  else feedback.push("At least 8 characters");

  if (/\d/.test(password)) score++;
  else feedback.push("At least one number");

  if (/[a-z]/.test(password)) score++;
  else feedback.push("At least one lowercase letter");

  if (/[A-Z]/.test(password)) score++;
  else feedback.push("At least one uppercase letter");

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
  else feedback.push("At least one special character");

  let strength = "Weak";
  if (score >= 5) strength = "Strong";
  else if (score >= 3) strength = "Medium";

  return { strength, score, feedback };
};

/**
 * Validate all registration fields
 * @param {object} fields - Object containing email, username, password, confirmPassword
 * @returns {object} {isValid: boolean, errors: object}
 */
export const validateRegistrationFields = (fields) => {
  const errors = {};
  let isValid = true;

  const emailValidation = validateEmailField(fields.email_address || "");
  if (!emailValidation.isValid) {
    errors.email_address = emailValidation.error;
    isValid = false;
  }

  const usernameValidation = validateUsernameField(fields.username || "");
  if (!usernameValidation.isValid) {
    errors.username = usernameValidation.error;
    isValid = false;
  }

  const passwordValidation = validatePasswordField(fields.password || "");
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
    isValid = false;
  }

  const confirmPasswordValidation = validateConfirmPasswordField(
    fields.password || "",
    fields.confirmPassword || ""
  );
  if (!confirmPasswordValidation.isValid) {
    errors.confirmPassword = confirmPasswordValidation.error;
    isValid = false;
  }

  return { isValid, errors };
};
